import React, { forwardRef, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrayHelpers, FieldArray, useFormikContext } from 'formik';
import { CustomButton, FormTextField, RoundedAvatar } from '../../../../../components';
import { useMutation, useQuery } from '@tanstack/react-query';
import { DraggableAttributes } from '@dnd-kit/core';
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';

import { AddIcon } from '../../../../../assets/icons/AddIcon';
import { ClassTeam, ClassValues } from '../ClassesTag/ClassesTag';
import {
  Accordion,
  Card,
  InputGroup,
  Form,
  Alert,
  OverlayTrigger,
  Tooltip,
  Badge,
} from 'react-bootstrap';
import { CustomAccordionHeader } from '../../../common/components/CustomAccordionHeader';
import { MemberTournamentCard } from '../../../../../components';
import { useAppDispatch, useAppSelector } from '../../../../../hooks/redux';
import { classTypesData } from '../../../../../constant/class';
import { ModalAction, ModalName } from '../../../../../types/modal';
import { openModal } from '../../../../../features/modal/modalSlice';
import { CloseCircle } from '../../../../../assets/icons/CloseCircle';
import './index.scss';
import { ClassFormatEnum, ClassTypeEnum, TypeError } from '../../../../../types/enums';
import ImageUploading, { ImageListType } from 'react-images-uploading';
import { uploadImage } from '../../../../../apis/tournament';
import { RootState } from '../../../../../app/store';

import { generateTeamName } from '../../../../../utils/generateTeamName';
import { teamAfterUnsavedPlayerRemoval } from '../../../../../utils/teamRosterForm';
import { getDisplayName } from '../../../../../utils/userDisplayName';
import { TicketDisplayStatus } from '../../../../../components/MemberTournamentCard/MemberTournamentCard';
import { ArrowUpRight, Ban, GripVertical, PlusSquareFill, Scissors } from 'react-bootstrap-icons';
import { transferTeamToClass, splitTeam } from '../../../../../apis/class';
import { queryClient } from '../../../../../apis/api.config';
import { Notification } from '../../../../../hooks/useNotification';
import { CombineType, DraggableTeamItemProps } from './DraggableTeamItem';
import { fetchTicketsForClass } from '../../../../../apis/ticket';
import { useClassRefresh } from '../ClassesTag/ClassRefreshContext';
import { isTemporaryClassId } from '../../../../../utils/common.helpers';
import { TicketRoleEnum } from '../../../../../types/Ticket';

type TeamItemProps = Omit<DraggableTeamItemProps, 'setIsCombineTarget' | 'canCombineFn'> & {
  draggableProps?: DraggableAttributes & (SyntheticListenerMap | undefined);
  dragTargetProps?: {
    style?: {
      transform?: string | undefined;
      transition?: string | undefined;
    };
  };
  combineStatus: CombineType | null;
};

export const TeamItem = forwardRef<HTMLDivElement, TeamItemProps>(
  (
    {
      team,
      index,
      push,
      remove,
      classId,
      teams,
      setField,
      draggableProps,
      dragTargetProps,
      combineStatus,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const [activeKey, setActiveKey] = useState('' as any);
    const classRefresh = useClassRefresh();
    const currentType = useAppSelector((state: RootState) => state.class.currentType);
    const classCollection = useAppSelector((state: RootState) => state.class.classCollection);

    const type = classTypesData.find((el) => el.value === currentType);
    const dispatch = useAppDispatch();
    const isTeamLeague = type?.value === ClassTypeEnum.TEAMS;
    const { values: classFormValues } = useFormikContext<ClassValues>();
    const maxTeamRoster = Number(classFormValues?.maxTeamRoster) || 4;
    // Qualified / Consolation copies keep tickets on the parent class.
    const isSubClass = Boolean(classFormValues.parentClassId);

    const { data } = useQuery({
      queryKey: ['tickets', classId],
      queryFn: () => fetchTicketsForClass(classId?.toString() || ''),
      enabled: !!classId && !isTemporaryClassId(classId),
    });

    const prefix = 'creativeDetail-';
    const inputPrefix = 'input-';
    const modalPrefix = 'modal-';
    const buttonPrefix = 'button-';

    const handleOpenModal = (teamId: string | number) => {
      dispatch(
        openModal({
          currentModal: ModalAction.ADD_MEMBER,
          modalOpen: true,
          data: {
            teams,
            setField,
            teamId,
            type,
            maxTeamRoster,
            title: t(modalPrefix + 'addPlayers'),
            modalName: ModalName.ADD_TEAM_PLAYERS_CLASS,
          },
        })
      );
    };

    const handleOpenCaptainModal = (
      teamId: string | number,
      rosterOnly: boolean,
      changeCaptain: boolean
    ) => {
      dispatch(
        openModal({
          currentModal: ModalAction.ADD_CAPTAIN,
          modalOpen: true,
          data: {
            teams,
            setField,
            teamId,
            type,
            maxTeamRoster,
            rosterOnly,
            title: t(modalPrefix + (changeCaptain ? 'changeCaptain' : 'addCaptain')),
            modalName: ModalName.ADD_TEAM_CAPTAIN_CLASS,
          },
        })
      );
    };

    const onChange = (imageList: ImageListType, id: string | number) => {
      const formData = new FormData();
      formData.append('file', imageList[0]?.file || '');
      imageList[0].file &&
        uploadImage(formData, 'team')
          .then((data) => {
            const updatedTeams = teams.map((team) =>
              team.id === id ? { ...team, logo: data.url } : team
            );
            setField('teams', updatedTeams);
          })
          .catch((e) => {
            console.log(e);
          });
    };

    const transferMutation = useMutation({
      mutationFn: ({ classId, teamId }: { classId: number; teamId: number }) => {
        return transferTeamToClass(classId, teamId);
      },
      onSuccess: (data, variables) => {
        setField(
          'teams',
          teams.filter((team) => team.id.toString() !== variables.teamId.toString())
        );
        Notification(TypeError.success, t(prefix + 'transfer-success'));
      },
      onError: (error) => {
        Notification(TypeError.error, t(prefix + 'transfer-error'));
      },
    });

    const transferToTeam = (teamId: number, classId: number) => {
      transferMutation.mutate({ classId, teamId });
    };

    const splitMutation = useMutation({
      mutationFn: (teamId: number) => splitTeam(teamId),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['tickets', classId] });
        Notification(TypeError.success, t(prefix + 'split-success'));
        classRefresh?.();
      },
      onError: () => {
        Notification(TypeError.error, t(prefix + 'split-error'));
      },
    });

    const handleSplitTeamModal = (team: ClassTeam) => {
      dispatch(
        openModal({
          currentModal: ModalAction.SPLIT_TEAM,
          modalOpen: true,
          data: {
            team,
            onSplit: () => splitMutation.mutate(team.id),
          },
        })
      );
    };

    const handleTransferTeamModal = (team: ClassTeam) => {
      dispatch(
        openModal({
          currentModal: ModalAction.TRANSFER_TEAM,
          modalOpen: true,
          data: {
            team,
            transferToTeam,
          },
        })
      );
    };
    const isTeamFull =
      type?.value === ClassTypeEnum.TEAMS && (team.users?.length ?? 0) >= maxTeamRoster;

    const isPlayersBtnDisabled =
      (type?.value === ClassTypeEnum.SINGLES && team.users?.length >= 1) ||
      (type?.value === ClassTypeEnum.DOUBLES && team.users?.length >= 2) ||
      isTeamFull;

    const hasCaptain = Boolean(team.captain?.[0]);
    const showChangeCaptain = hasCaptain;

    const backendClass = classCollection?.find((c) => c.classId === classId);

    const backendTeam = backendClass?.teams?.find((t) => t.id === team.id);

    // Singles/doubles: each backend-known user needs a personal ticket.
    // TEAMS: capacity is one REGISTRATION per team — do not flag roster members.
    const playersMissingTickets =
      team.users.filter(
        (u) =>
          backendTeam?.users?.some((bu) => bu.id === u.id) &&
          !data?.find((t) => t.teamId === team.id && t.user?.id === u.id)
      ) || [];

    const hasTeamRegistration = !!data?.some(
      (t) =>
        t.teamId === team.id &&
        (t.ticketRole === TicketRoleEnum.REGISTRATION || t.ticketRole == null)
    );
    const teamMissingRegistration = !!backendTeam && !hasTeamRegistration;

    const formPlayersJoined = team.users.map((u) => u.id).join(',');
    const backendPlayersJoined = backendTeam?.users.map((u) => u.id).join(',');
    const formSeed = team.seed;
    const backendSeed = backendTeam?.seed;

    const hasUnsavedTeamChanges =
      team.name !== backendTeam?.name ||
      formPlayersJoined !== backendPlayersJoined ||
      formSeed !== backendSeed;

    const transferButton = (
      <CustomButton
        className="d-flex align-items-center mr-2"
        btnText={t(prefix + 'transferToAnotherClass')}
        variant="outline-dark"
        onClick={() => {
          handleTransferTeamModal(team);
        }}
        disabled={hasUnsavedTeamChanges}
      >
        <ArrowUpRight />
      </CustomButton>
    );

    const displayTicketWarning =
      !isSubClass && (isTeamLeague ? teamMissingRegistration : !!playersMissingTickets?.length);

    const totalRatedPlayers = team.users.reduce(
      (acc, user) => {
        if (
          (type?.value === ClassTypeEnum.DOUBLES ||
            backendClass?.format === ClassFormatEnum.BOX_LEAGUE ||
            backendClass?.format === ClassFormatEnum.BOX_LEAGUE_ELIM) &&
          user.duprRatingDoubles
        ) {
          const rating = parseFloat(user.duprRatingDoubles);
          if (!isNaN(rating)) {
            acc.totalRating += rating;
            acc.totalRatedUsers++;
          }
        } else if (
          type?.value === ClassTypeEnum.SINGLES &&
          backendClass?.format !== ClassFormatEnum.BOX_LEAGUE &&
          backendClass?.format !== ClassFormatEnum.BOX_LEAGUE_ELIM &&
          user.duprRatingSingles
        ) {
          const rating = parseFloat(user.duprRatingSingles);
          if (!isNaN(rating)) {
            acc.totalRating += rating;
            acc.totalRatedUsers++;
          }
        }
        return acc;
      },
      { totalRating: 0, totalRatedUsers: 0 }
    );

    const totalDuprRating =
      totalRatedPlayers.totalRatedUsers > 0 ? totalRatedPlayers.totalRating.toFixed(3) : 'N/A';

    const dragIcon = useMemo(() => {
      switch (combineStatus) {
        case CombineType.COMBINABLE:
          return <PlusSquareFill size={20} />;
        case CombineType.NOT_COMBINABLE:
          return <Ban />;
        default:
          return <GripVertical />;
      }
    }, [combineStatus]);

    return (
      <div key={team.id} ref={ref} {...dragTargetProps} className="container-fluid px-0 my-1">
        <Accordion alwaysOpen={false} activeKey={activeKey} onSelect={setActiveKey}>
          <Card>
            <Card.Header className="p-0 bg-white border-0">
              <CustomAccordionHeader eventKey={index.toString()}>
                <InputGroup className="flex-nowrap">
                  <div
                    className="grab-handle d-flex align-items-center justify-content-center h-"
                    {...draggableProps}
                  >
                    {dragIcon}
                  </div>
                  <Form.Text className="mt-0">
                    <div className="d-flex p-2 h-100 flex-column justify-content-center">
                      {(team.logo && (
                        <RoundedAvatar name="flag" avatar={team.logo} alt="Team logo" />
                      )) || <div className="raqt__seed-holder">{team.seed}</div>}
                    </div>
                  </Form.Text>
                  <div className="d-flex align-items-center">
                    <OverlayTrigger
                      overlay={<Tooltip>{t(prefix + 'averageDuprRating')}</Tooltip>}
                      placement="top"
                    >
                      <Badge className="mx-2">{totalDuprRating}</Badge>
                    </OverlayTrigger>
                  </div>
                  <FormTextField
                    className="py-0 border-1 w-100 border-white"
                    index={index}
                    name={`teams.${index}.name`}
                    placeholder={t(inputPrefix + 'placeholderTeamName')}
                    type="text"
                  />
                  <input type="hidden" name={`teams.${index}.shuffled`} />
                </InputGroup>
              </CustomAccordionHeader>
            </Card.Header>
            <Accordion.Collapse eventKey={index.toString()}>
              {(activeKey === index.toString() && (
                <Card.Body className="p-2">
                  <div className="w-100 d-flex flex-wrap flex-column flex-lg-row">
                    <div className="col-lg-3 d-flex flex-column flex-lg-row align-items-end align-items-lg-start px-0 px-lg-2">
                      <div className="d-flex flex-column">
                        {isTeamLeague && (
                          <>
                            {team.logo && (
                              <img
                                style={{
                                  objectFit: 'cover',
                                  width: '100%',
                                  maxWidth: '240px',
                                  aspectRatio: '1/1',
                                }}
                                className={'rounded-circle'}
                                src={team.logo}
                                alt={team.name + ' team logo'}
                              />
                            )}

                            <ImageUploading
                              value={[]}
                              onChange={(imageList: ImageListType) => {
                                onChange(imageList, team.id);
                              }}
                            >
                              {({ onImageUpload }) => (
                                <CustomButton
                                  variant="outline-dark"
                                  btnText={t(buttonPrefix + 'addTeamIcon')}
                                  cb={onImageUpload}
                                />
                              )}
                            </ImageUploading>
                          </>
                        )}
                        {isTeamLeague && (
                          <CustomButton
                            className="d-flex justify-content-evenly align-items-center"
                            variant="outline-dark"
                            btnText={t(
                              buttonPrefix + (showChangeCaptain ? 'changeCaptain' : 'addCaptain')
                            )}
                            cb={() => {
                              handleOpenCaptainModal(team.id, hasCaptain, showChangeCaptain);
                            }}
                          >
                            <AddIcon />
                          </CustomButton>
                        )}
                        <CustomButton
                          disabled={isPlayersBtnDisabled}
                          className="d-flex justify-content-evenly align-items-center"
                          variant="outline-dark"
                          btnText={t(buttonPrefix + 'addPlayers')}
                          cb={() => {
                            handleOpenModal(team.id);
                          }}
                        >
                          <AddIcon />
                        </CustomButton>
                      </div>
                    </div>
                    <div
                      className={`col d-flex flex-wrap ${
                        isTeamLeague ? 'flex-column col-lg-4' : ''
                      } px-0`}
                    >
                      {isTeamLeague &&
                      team.captain &&
                      team.captain.length > 0 &&
                      team.captain[0]?.id ? (
                        <>
                          <h5 className="font-weight-normal mb-0">{t(prefix + 'teamCaptain')}</h5>
                          <div>
                            <React.Fragment>
                              <MemberTournamentCard
                                ticketStatus="notUsed"
                                member={team.captain[0]}
                                type={type?.value}
                              />
                            </React.Fragment>
                          </div>
                        </>
                      ) : (
                        <div className="d-flex justify-content-center align-items-center">
                          <p className="fw-lighter mb-0">{t(prefix + 'chooseCaptain')}</p>
                        </div>
                      )}
                      <FieldArray name={`teams.${index}.users`}>
                        {({ remove, replace }: ArrayHelpers) => {
                          return team.users.length ? (
                            <>
                              {team.users
                                .filter((v, index) => {
                                  const maxLength = type?.value === ClassTypeEnum.SINGLES ? 1 : 2;
                                  return isTeamLeague ? true : index < maxLength;
                                })
                                .map((user, i) => {
                                  let ticket = data?.find(
                                    (t) => t.teamId === team.id && t.user?.id === user.id
                                  );
                                  // Check if the user exists in the original class data to determine if they're newly added
                                  const isNewlyAddedUser = !classCollection
                                    ?.find((c) => c.classId === classId)
                                    ?.teams?.find((t) => t.id === team.id)
                                    ?.users?.find((u) => u.id === user.id);

                                  let ticketStatus: TicketDisplayStatus = ticket
                                    ? 'active'
                                    : 'missing';
                                  if (isNewlyAddedUser) {
                                    // If player is already in another team, we use that ticket. Otherwise a new one will be created on save.
                                    ticket = data?.find((t) => t.user?.id === user.id);
                                    if (ticket) {
                                      ticketStatus = 'active';
                                    } else {
                                      ticketStatus = 'pending';
                                    }
                                  }
                                  const isRegistrationTicket =
                                    !!ticket &&
                                    ticket.teamId === team.id &&
                                    (ticket.ticketRole === TicketRoleEnum.REGISTRATION ||
                                      ticket.ticketRole == null);
                                  if (type?.value === ClassTypeEnum.TEAMS) {
                                    // Only the team registration holder shows a ticket affordance;
                                    // roster members stay quiet (capacity is team-level).
                                    ticketStatus = isRegistrationTicket ? 'active' : 'notUsed';
                                  }
                                  if (isSubClass) {
                                    ticketStatus = 'notUsed';
                                  }
                                  return (
                                    <div key={user.id}>
                                      {i === 0 && isTeamLeague ? (
                                        <h5 className="font-weight-normal mb-0 mt-3">
                                          {t(prefix + 'teamPlayers')}
                                        </h5>
                                      ) : null}
                                      <React.Fragment>
                                        <MemberTournamentCard
                                          ticketStatus={ticketStatus}
                                          showTeamRegistrationBadge={
                                            isTeamLeague && isRegistrationTicket
                                          }
                                          onClick={() => {
                                            // If user hasn't been saved to backend in this class yet, remove directly
                                            const isNewlyAddedUser = !classCollection
                                              ?.find((c) => c.classId === classId)
                                              ?.teams?.find((t) => t.id === team.id)
                                              ?.users?.find((u) => u.id === user.id);
                                            if (isNewlyAddedUser) {
                                              setField(
                                                'teams',
                                                teams.map((t) => {
                                                  if (t.id !== team.id) {
                                                    return t;
                                                  }
                                                  const next = teamAfterUnsavedPlayerRemoval(
                                                    t,
                                                    user.id
                                                  );
                                                  return {
                                                    ...next,
                                                    name: generateTeamName(next.users, type, t),
                                                  };
                                                })
                                              );
                                              return;
                                            }
                                            dispatch(
                                              openModal({
                                                currentModal: ModalAction.REMOVE_PLAYER,
                                                modalOpen: true,
                                                data: {
                                                  title: 'Remove player',
                                                  team,
                                                  user,
                                                  teams,
                                                  setField,
                                                  type,
                                                  tickets: data,
                                                  classId,
                                                },
                                              })
                                            );
                                          }}
                                          member={user}
                                          ticket={ticket}
                                          type={type?.value}
                                        />
                                      </React.Fragment>
                                    </div>
                                  );
                                })}
                            </>
                          ) : (
                            <div className="d-flex justify-content-center align-items-center">
                              <p className="fw-lighter mb-0">{t(prefix + 'choosePlayers')}</p>
                            </div>
                          );
                        }}
                      </FieldArray>
                    </div>
                  </div>
                  <div className="col-5 col-lg-3 p-2">
                    <FormTextField
                      label={t(prefix + 'seed')}
                      className="py-0 border-1 w-100"
                      index={index}
                      name={`teams.${index}.seed`}
                      placeholder={t(inputPrefix + 'placeholderSeed')}
                      type="text"
                    />
                  </div>
                  {displayTicketWarning && (
                    <Alert variant="warning">
                      <Alert.Heading>{t(prefix + 'ticketsWarningTitle')}</Alert.Heading>

                      {isTeamLeague && teamMissingRegistration ? (
                        <h6 className="mb-0">
                          {t(prefix + 'ticketsWarningMissingTeamRegistration')}
                        </h6>
                      ) : null}

                      {!isTeamLeague && playersMissingTickets?.length > 0 && (
                        <>
                          <h6 className="mb-0">{t(prefix + 'ticketsWarningMissingTickets')}</h6>
                          <ul className="mb-0">
                            {playersMissingTickets.map((player) => (
                              <li key={player.id}>{getDisplayName(player)}</li>
                            ))}
                          </ul>
                        </>
                      )}
                    </Alert>
                  )}
                  <div className="d-flex justify-content-start justify-content-lg-end">
                    {hasUnsavedTeamChanges ? (
                      <OverlayTrigger
                        overlay={
                          <Tooltip id={`transfer-tooltip-${team.id}`}>
                            {t(prefix + 'cantTransferDirtyForm')}
                          </Tooltip>
                        }
                        placement="top"
                      >
                        <span>{transferButton}</span>
                      </OverlayTrigger>
                    ) : (
                      transferButton
                    )}
                    {currentType === ClassTypeEnum.DOUBLES &&
                      team.users.length >= 2 &&
                      (hasUnsavedTeamChanges ? (
                        <OverlayTrigger
                          overlay={
                            <Tooltip id={`split-tooltip-${team.id}`}>
                              {t(prefix + 'cantSplitDirtyForm')}
                            </Tooltip>
                          }
                          placement="top"
                        >
                          <span>
                            <CustomButton
                              className="d-flex align-items-center mr-2"
                              btnText={t(prefix + 'splitTeam')}
                              variant="outline-dark"
                              onClick={() => handleSplitTeamModal(team)}
                              disabled={hasUnsavedTeamChanges}
                            >
                              <Scissors />
                            </CustomButton>
                          </span>
                        </OverlayTrigger>
                      ) : (
                        <CustomButton
                          className="d-flex align-items-center mr-2"
                          btnText={t(prefix + 'splitTeam')}
                          variant="outline-dark"
                          onClick={() => handleSplitTeamModal(team)}
                        >
                          <Scissors />
                        </CustomButton>
                      ))}
                    <CustomButton
                      className="d-flex align-items-center"
                      btnText={t(buttonPrefix + 'removeTeam')}
                      variant="outline-dark"
                      onClick={() => {
                        remove(index);
                      }}
                    >
                      <CloseCircle />
                    </CustomButton>
                  </div>
                </Card.Body>
              )) || (
                <Card.Body>
                  <div className="team-section__dummy"></div>
                </Card.Body>
              )}
            </Accordion.Collapse>
          </Card>
        </Accordion>
      </div>
    );
  }
);
